import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { generateVariableSubActivityData, ActivityDataModel, checkVariableSubAnswer } from './VariableSub.utils'

interface UseVariableSubReturnType {
  variable: string
  equationCoefficients: number[]
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseVariableSubProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseVariableSub({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseVariableSubProps): UseVariableSubReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [activityData, setActivityData] = useState<ActivityDataModel>({
    equationCoefficients: [],
    coefficientTotal: 0,
    variable: '',
    coefficientString: '',
    maxHistoryLength: 10,
  })
  const [answer, setAnswer] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateVariableSubActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(checkVariableSubAnswer(answer, activityData.coefficientTotal, activityData.variable))
  }, [onAnswerStateChange, answer, activityData])

  const onInputChange = useCallback((value: string) => {
    setAnswer(value)
  }, [])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) =>
      [activityData.coefficientString, ...prevState].slice(0, activityData.maxHistoryLength),
    )
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.coefficientString, activityData.maxHistoryLength])

  return {
    equationCoefficients: activityData.equationCoefficients,
    variable: activityData.variable,
    answer,
    formValid: answer !== '',
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseVariableSub
